import { P2GBox } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ExtraBase,
  GetQuoteValidationResponse,
  QuoteApiModel,
  QuoteExtraApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { IossNumber, IossNumberSource } from '../../../lib/consts/ItemMetaConstants';
import AlertWithIcon from '../../AlertWithIcon';
import { DeliveredDutyPaidExtra } from '../../ExtrasHelper';
import Loading from '../../../../../components/loading/Loading';
import { CustomsSelection } from '../CustomsSelection';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { CustomsDuties } from '../CustomsDuties';
import { getSelectedQuoteOption } from '../generateProtectionKey';
import { EditType } from '../item/EditType';
import { IossIsRequired } from '../../../lib/helpers/IossUtils';
import { uid } from '../../../lib/helpers/uid';
import ShouldRequoteOnTaxStatusChange from '../../../lib/helpers/ShouldRequoteOnTaxStatusChange';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { ddpQuoteKey } from '~/lib/api/smartsend/cache-keys';
import { SmartSendProvider } from '~/lib/consts/providers';

interface props {
  onUpdate?: (item: SmartSendItemApiModel) => void;
  item: SmartSendItemApiModel;
  changeTab: (action: EditType) => void;
  getValidation: () => Promise<GetQuoteValidationResponse>;
  getDeliveryDutyPaidCalculation: () => Promise<{ DutyValue: number; VatValue: number; AdminFee?: number }>;
  getQuote: () => Promise<QuoteApiModel>;
}

export const ItemCustomsPanel: React.FC<props> = (props) => {
  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState<GetQuoteValidationResponse>(null);
  const [availableExtras, setAvailableExtras] = useState<QuoteExtraApiModel[]>([]);
  const [extras, setExtras] = useState<ExtraBase[]>([]);
  const [quoteKey, setQuoteKey] = useState<string>(uid());
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const dduUnavailable = useMemo(
    () =>
      (validation?.Quote?.Service?.Tags?.includes('dduUnavailable') ||
        validation?.Quote?.Tags?.includes('dduUnavailable')) ??
      false,
    [validation]
  );

  const provider = useMemo(() => validation?.Quote?.Service?.Provider ?? '', [validation]);

  const getData = useCallback(async () => {
    const v = await props.getValidation();
    setValidation(v);

    setLoading(false);
    // Needs a reshuffle TODO
    const q = await props.getQuote();
    if (q) {
      const qo = getSelectedQuoteOption(props.item, q) || q.Options[0];
      const include = [DeliveredDutyPaidExtra];
      setAvailableExtras(qo.AvailableExtras.concat(qo.Extras).filter((x) => include.includes(x.Id)));
      setExtras(qo.AvailableExtras.concat(qo.Extras).filter((x) => props.item.Extras.map((e) => e.Id).includes(x.Id)));
    }

    setLoading(false);
  }, [props]);

  useEffect(() => {
    getData();
  }, [quoteKey]);

  if (loading) {
    return <Loading />;
  }

  return (
    <SidePanelSections>
      <P2GBox textAlign="left">
        {validation ? (
          <CustomsSelection
            sender={props.item.Sender}
            recipient={props.item.Recipient}
            itemMeta={props.item.Meta}
            marketplaceIds={props.item.MarketplaceOrderLines.map((x) => x.Source?.Id)}
            validation={{
              SenderValidation: validation.SenderValidation,
              RecipientValidation: validation.RecipientValidation,
              ItemValidation: validation.ItemValidation
            }}
            onUpdate={(s, r, m) => {
              const i = { ...props.item, Sender: { ...s }, Recipient: { ...r } };

              for (const p in m) {
                i.Meta[p] = m[p];
              }
              props.onUpdate(i);

              if (ShouldRequoteOnTaxStatusChange(props.item, i)) {
                setQuoteKey(uid());
              }
            }}
            duties={
              provider === SmartSendProvider && (
                <Suspense>
                  <CustomsDuties
                    ddpQuoteKey={ddpQuoteKey(SmartSendProvider, SmartSendProvider, props.item)}
                    currencyCode={validation.Quote?.CurrencyCode ?? props.item.CurrencyCode}
                    dduUnavailable={dduUnavailable}
                    recipientIsoCode={props.item.Recipient?.CountryIsoCode}
                    changeTab={props.changeTab}
                    canGetDdpCalculation={
                      props.item.MarketplaceOrderLines.filter((x) => !!x.Product.TariffCode).length ===
                      props.item.MarketplaceOrderLines.length
                    }
                    iossNumber={props.item.Meta[IossNumber]?.Value}
                    onIossUpdate={(v) => {
                      const i = { ...props.item };
                      i.Meta[IossNumber] = { Type: 'String', Value: v };
                      props.onUpdate(i);
                    }}
                    toggleExtra={(e, include) => {
                      if (e) {
                        const ex = [...extras].filter((x) => x.Id !== e.Id);
                        const uEx = include ? [...ex, e] : ex;
                        setExtras(uEx);
                        const i = { ...props.item };
                        i.Extras = uEx;
                        props.onUpdate(i);
                      }
                    }}
                    iossNumberRequired={IossIsRequired(validation)}
                    iossNumberEnabled={!!validation.ItemValidation.find((x) => x.name === IossNumber)}
                    iossNumberValidation={validation.ItemValidation.find((x) => x.name === IossNumber)?.pattern}
                    iossNumberSource={props.item.Meta[IossNumberSource]?.Value}
                    availableExtras={availableExtras}
                    extras={extras}
                    getDdpCost={props.getDeliveryDutyPaidCalculation}
                    wrapper={(x) => (
                      <SidePanelSection title={t('TAX_DUTIES_SUBTITLE', { defaultValue: 'Tax And Duties:' })}>
                        {x}
                      </SidePanelSection>
                    )}
                  />
                </Suspense>
              )
            }
          />
        ) : (
          <P2GBox mt={2}>
            <Loading />
          </P2GBox>
        )}
      </P2GBox>
      <SidePanelSection title={t('TARIFF_CODES_SUBTITLE', { defaultValue: 'Tariff Codes' })}>
        <AlertWithIcon>
          <P2GText variant="body2">
            <Trans
              i18nKey="TARIFF_CODES_ALERT"
              t={t}
              components={{ a: <P2GLink onClick={() => props.changeTab('CONTENTS')} /> }}
              defaults={
                'Items in your parcel must have the correct Tariff codes. <br />To add/amend these codes, go to the <a>Contents</a> tab.'
              }
            />
          </P2GText>
        </AlertWithIcon>
      </SidePanelSection>
    </SidePanelSections>
  );
};
